import { Injectable, OnDestroy } from '@angular/core';
import {
  Store,
  Actions,
  ofActionCompleted,
  ofActionDispatched,
  ofActionSuccessful,
} from '@ngxs/store';
import { Subject, defer, iif, merge, of } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { FlowStorage } from '@pe/checkout/storage';
import { FlowCloneReason, FlowStateEnum } from '@pe/checkout/types';

import { SetPaymentComplete, SetPrevAction } from '../checkout';
import { CloneFlow, FinishFlow, FlowState } from '../flow';
import { SplitState } from '../split';

import { PaymentProxyService } from './payment-proxy.service';
import {
  GetPaymentOptions,
  PatchFormState,
  PatchPaymentDetails,
  PatchPaymentResponse,
  SetFormState,
  SetPaymentDetails,
  SetPaymentError,
  SetPaymentOptions,
  SetPaymentService,
  SetPayments,
  SubmitPayment,
  UpdatePayment,
} from './payment.actions';
import { PaymentState } from './payment.state';

@Injectable({
  providedIn: 'root',
})
export class PaymentActionHandler implements OnDestroy{
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly flowStorage: FlowStorage,
    private readonly paymentProxy: PaymentProxyService,
  ) {
    const patchStorage$ = this.store.select(FlowState.flow).pipe(
      filter(value => !!value),
      tap((flow) => {
        const data = this.flowStorage.getData(flow.id, 'paymentData');
        !!data && this.store.dispatch(new SetPayments(data));
      }),
      switchMap(flow => this.actions$.pipe(
        ofActionSuccessful(
          PatchPaymentDetails,
          SetPaymentDetails,
          PatchPaymentResponse,
          SetPaymentOptions,
          PatchFormState,
          SetFormState,
          SetPaymentError,
        ),
        tap(() => {
          const state = this.store.selectSnapshot(PaymentState);
          this.flowStorage.setData(flow.id, 'paymentData', state);
        }),
      )),
    );

    const setPaymentService$ = this.actions$.pipe(
      ofActionDispatched(SetPaymentService),
      tap(({ paymentService }) => {
        this.paymentProxy.setPaymentService(paymentService);
      }),
    );

    const submitPayment$ = this.actions$.pipe(
      ofActionDispatched(SubmitPayment),
      filter(() => {
        const flow = this.store.selectSnapshot(FlowState.flow);

        return flow.state !== FlowStateEnum.FINISH;
      }),
      switchMap(() => iif(
        () => {
          const { splits, connectionId } = this.store.selectSnapshot(FlowState.flow);
          const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
          if (!splits?.length) {
            return false;
          }
          const split = splits.find(split => split.connectionId === connectionId && !split.isPaid);
          const response = this.store.selectSnapshot(PaymentState.responseByPaymentMethod)(paymentMethod);

          return split?.paymentMethod === paymentMethod && !!response;
        },
        defer(() => this.store.dispatch(new CloneFlow({
          skipData: true,
          reason: FlowCloneReason.ChangeNonFinishedPayment,
          redirect: false,
        })).pipe(
          switchMap(() => this.processPayment())
        )),
        this.processPayment()
      )),
    );

    const paymentErrors$ = this.actions$.pipe(
      ofActionCompleted(
        SubmitPayment,
        GetPaymentOptions,
        UpdatePayment,
      ),
      filter(({ result }) => !!result?.error),
      tap(({ action, result: { error } }) => {
        this.store.dispatch([
          new SetPaymentError((error as any)),
          new SetPaymentComplete(true, action),
          new SetPrevAction(this.prevActionFactory(action)),
        ]);
      }),
    );

    merge(
      patchStorage$,
      setPaymentService$,
      submitPayment$,
      paymentErrors$,
    ).pipe(
      takeUntil(this.destroy$),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  prevActionFactory(action: SubmitPayment | GetPaymentOptions | UpdatePayment): InstanceType<any> | null {
    return action instanceof GetPaymentOptions ? new GetPaymentOptions() : null;
  }

  private processPayment() {
    const flow = this.store.selectSnapshot(FlowState.flow);
    const splits = this.store.selectSnapshot(SplitState.splits);

    return this.paymentProxy.postPayment().pipe(
      switchMap(() => iif(
        () => !splits?.length || !flow.splits?.length || !flow.order.remainingAmount,
        defer(() => this.store.dispatch([
          new FinishFlow(),
        ])),
        of(null),
      ).pipe(
        tap(() => {
          this.paymentProxy.redirect();
          this.store.dispatch(new SetPaymentError(null)).pipe(
            switchMap(() => this.store.dispatch(new SetPaymentComplete())),
          );
        }),
      )),
    );
  }
}
